import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';

import ConsumerLayout from '../consumer-layout/ConsumerLayout';
import Header from './components/header/Header';
import useBindAction from '../../hooks/useBindAction';
import { changeActiveGenStatus, changeId8Tab, resetId8Params } from '../../redux/id8/id8Reducer';
import {
  generateMessagesConsumer,
  replyId8Consumer,
  resetSessionConsumer,
  saveMessageChangesConsumer,
} from '../../redux/consumer/id8/consumerId8Actions';
import GenerationSection from '../../id8/components/generation-section/GenerationSection';
import { uploadFile } from '../../redux/scout/scoutActions';
import { clearConsumerProjectStatus } from '../../redux/global/globalActions';
import CommonTabs from '../../components/common-tabs/CommonTabs';
import CommonTabPanel from '../../components/common-tabs/CommonTabPanel';
import { ID8_GEN_TYPES, ID8_TABS, PROJECT_TYPES, PROJECT_VARIANTS, PROMPT_TYPE } from '../../constants';
import FramesTab from './components/frames/FramesTab';
import IdeasTab from './components/ideas/IdeasTab';
import webSocketManager from '../../service/WebSocketManager';
import UnsavedModalController from '../../id8/components/unsaved-modal-controller/UnsavedModalController';
import { ProjectProvider } from '../../context/ProjectContext';
import useId8Tabs from '../../id8/hooks/useId8Tabs';
import RecreateTab from './components/recreate/RecreateTab';
import { clearId8ImageCaches } from '../../utils/clearId8ImageCaches';

import scss from './Id8.module.scss';

function Id8() {
  const dispatch = useDispatch();

  const id8Tab = useSelector((state) => state.id8.id8Tab);
  const isDirty = useSelector((state) => state.id8.isDirty);
  const { projectName, projectPassword } = useSelector((state) => state.consumer.id8);

  const [isShowGenerationForm, setIsShowGenerationForm] = useState(true);
  const [isShowUnsavedModal, setIsShowUnsavedModal] = useState(false);

  const { triggerAction, bindAction, resetAction } = useBindAction(isDirty, () => handleShowModal());

  const TABS = useId8Tabs();

  const initialImagesGenerationStatusCheck = async () => {
    const response = await axios.post(`/api/consumer/project/status/is/id8/content/generating`, {
      projectRequest: {
        projectName,
        projectPassword,
      },
      request: PROMPT_TYPE.IDEA,
    });

    dispatch(changeActiveGenStatus({ genType: ID8_GEN_TYPES.IMAGES, status: response.data }));
  };

  const handleChangeTabs = (_, newTab) => {
    bindAction(() => dispatch(changeId8Tab(newTab)));
  };

  const handleShowModal = () => {
    setIsShowUnsavedModal(true);
  };

  const handleSaveModal = async () => {
    await dispatch(saveMessageChangesConsumer());
  };

  const handleResetSession = () => {
    dispatch(resetId8Params());
    dispatch(clearConsumerProjectStatus({ projectName, projectPassword }));
    clearId8ImageCaches();
    webSocketManager.closeConsumerProjectConnection(projectPassword);
  };

  useEffect(() => {
    initialImagesGenerationStatusCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      handleResetSession();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.addEventListener('beforeunload', handleResetSession);

    return () => {
      window.removeEventListener('beforeunload', handleResetSession);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ConsumerLayout Header={Header()}>
      <ProjectProvider projectVariant={PROJECT_VARIANTS.CONSUMER} projectType={PROJECT_TYPES.ID8}>
        <GenerationSection
          isShowGenerationForm={isShowGenerationForm}
          onShowGenerationForm={setIsShowGenerationForm}
          onReply={replyId8Consumer}
          onGenerateMessages={generateMessagesConsumer}
          onResetSession={resetSessionConsumer}
          onFileUpload={uploadFile}
        />

        <CommonTabs tabs={TABS} value={id8Tab} onChange={handleChangeTabs} className={scss.tabs} isFullWidth />

        <CommonTabPanel value={id8Tab} index={ID8_TABS.IDEAS} className={scss.tabPanel}>
          <IdeasTab bindAction={bindAction} isShowGenerationForm={isShowGenerationForm} />
        </CommonTabPanel>

        <CommonTabPanel value={id8Tab} index={ID8_TABS.IMAGES} className={scss.tabPanel}>
          <FramesTab />
        </CommonTabPanel>

        <CommonTabPanel value={id8Tab} index={ID8_TABS.RECREATE} className={scss.tabPanel}>
          <RecreateTab />
        </CommonTabPanel>

        <CommonTabPanel value={id8Tab} index={ID8_TABS.LIKED} className={scss.tabPanel}>
          Liked tab
        </CommonTabPanel>

        <UnsavedModalController
          isShowUnsavedModal={isShowUnsavedModal}
          onChangeUnsavedModalVisibility={setIsShowUnsavedModal}
          resetAction={resetAction}
          triggerAction={triggerAction}
          onSaveChanges={handleSaveModal}
        />
      </ProjectProvider>
    </ConsumerLayout>
  );
}

export default Id8;
